'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation, api, useUi } from '@weareconceptstudio/core';
import { trackViewItem } from '@/libs/dataLayer';

/**
 * Component that tracks view_item events for products opened in menu pages
 * Detects:
 * 1. URL pattern: /menu/category-slug/product-slug/
 * 2. Popup opening with product slug from popupProps
 */
export default function MenuProductTracking() {
	const pathname = usePathname();
	const { selectedLang } = useTranslation();
	const { popupIsOpen, popupProps } = useUi();
	const trackedSlugsRef = useRef(new Set());

	// Track via URL pattern (when URL changes to product page)
	useEffect(() => {
		// Detect menu product URL pattern: /menu/category-slug/product-slug/ or /lang/menu/category-slug/product-slug/
		const menuProductPattern = /\/menu\/([^\/]+)\/([^\/]+)\/?$/;
		const match = pathname.match(menuProductPattern);

		if (match) {
			const productSlug = match[2];
			const trackingKey = `url-${productSlug}`;

			// Only track once per product slug
			if (!trackedSlugsRef.current.has(trackingKey)) {
				trackedSlugsRef.current.add(trackingKey);

				const fetchAndTrack = async () => {
					try {
						const { data } = await api.get({ url: `/product/${productSlug}`, lang: selectedLang });

						// Track view_item with product data
						trackViewItem(data, {
							variant: data.selectedVariant,
							option: data.selectedOption,
						});
					} catch (error) {
						console.error('Error fetching product for tracking:', error);
						// Remove from tracked set on error so it can retry
						trackedSlugsRef.current.delete(trackingKey);
					}
				};

				fetchAndTrack();
			}
		}
	}, [pathname, selectedLang]);

	// Track via popup opening (when popup opens with product slug in popupProps)
	useEffect(() => {
		if (popupIsOpen && popupProps?.slug) {
			// Check if we're on a menu page
			const isMenuPage = pathname.includes('/menu/');

			if (isMenuPage) {
				const productSlug = popupProps.slug;
				const trackingKey = `popup-${productSlug}`;

				// Only track once per product slug
				if (!trackedSlugsRef.current.has(trackingKey)) {
					trackedSlugsRef.current.add(trackingKey);

					// Wait a bit for popup to fully render
					const timeoutId = setTimeout(() => {
						const fetchAndTrack = async () => {
							try {
								const { data } = await api.get({ url: `/product/${productSlug}`, lang: selectedLang });

								trackViewItem(data, {
									variant: data.selectedVariant,
									option: data.selectedOption,
								});
							} catch (error) {
								console.error('Error fetching product for tracking:', error);
								trackedSlugsRef.current.delete(trackingKey);
							}
						};

						fetchAndTrack();
					}, 300);

					return () => clearTimeout(timeoutId);
				}
			}
		} else if (!popupIsOpen) {
			// Clear popup-related tracking when popup closes
			const keysToDelete = Array.from(trackedSlugsRef.current).filter((key) => key.startsWith('popup-'));
			keysToDelete.forEach((key) => trackedSlugsRef.current.delete(key));
		}
	}, [popupIsOpen, popupProps?.slug, pathname, selectedLang]);

	return null;
}
